/**
 * CRM module — map & address services (user steer 2026-07-13: addresses get a
 * dual List & Map view; lookup/autocomplete rides configurable providers;
 * ALL of it lives in the CRM MODULE, not the shell).
 *
 * In a real deployment these settings are the crm module's config rows
 * (`core.config`, fail-closed per CLAUDE.md §11) and the lookups run through
 * an edge worker so API keys never reach the browser. Here — console/fixture —
 * they persist per-browser and the `builtin` provider gives a deterministic,
 * OFFLINE address book so demos and e2e never depend on an external API.
 */
import { useSyncExternalStore } from 'react'
/* ── #524: these are SETTINGS, not browser storage ─────────────────────────
 * This module used to persist to `localStorage`, which meant the provider API
 * keys were per-BROWSER, plaintext, un-audited, admin-gated by nothing, and
 * invisible to `core.describe_settings()` — so `is_secret` (masking,
 * admin-gating) never applied to them. Same class as #267: `is_secret` protects
 * what is REGISTERED, so anything unregistered is protected by nothing.
 *
 * They are now declared in `crm/2026080402` and live in `core.setting`. The
 * localStorage path is DELETED rather than kept as a fallback — a fallback that
 * still writes to the browser is the defect with a longer name.
 *
 * The store stays synchronous (`getX()` is called from phone normalisation, a
 * hot path with no place to await), so it is a module-level cache HYDRATED once
 * at startup by `hydrateCrmSettings`, exactly as BrandSync hydrates the brand.
 * Before hydration the DEFAULTS apply, which is the honest answer: the offline
 * built-in providers, and no keys.
 */

/* ── #524 follow-up (crm 2026080422): there are no KEYS on this type ────────
 * `describe_settings` answers a secret with the string '••••••', never the
 * plaintext — so a key the browser reads back is a mask, and `mapEmbedUrl` was
 * interpolating it straight into a Google embed URL. Setting the key in the
 * Settings UI did not enable Google Maps; it broke the map, silently. An embed
 * carries its key in the page by construction, so there is no version of that
 * provider that is both working and governed, and Google Places had never
 * returned a result at all. Both providers and both keys are DELETED. What
 * remains is keyless and works: the OSM embed, the offline book, Nominatim, and
 * the keyless multi-pin SlippyMap.
 */
export interface GeoSettings {
  /** map renderer — OSM embeds are keyless, and there is no keyed alternative */
  mapProvider: 'osm' | 'none'
  /** address lookup: builtin = offline demo book · nominatim (OSM, keyless) */
  lookupProvider: 'builtin' | 'nominatim' | 'none'
}

const DEFAULTS: GeoSettings = { mapProvider: 'osm', lookupProvider: 'builtin' }

let state: GeoSettings = DEFAULTS
const subs = new Set<() => void>()

/** Cache-only. Used by the startup hydration; does NOT write to the server. */
export function applyGeoSettings(patch: Partial<GeoSettings>) {
  state = { ...state, ...patch }
  subs.forEach((f) => f())
}
export function useGeoSettings(): GeoSettings {
  return useSyncExternalStore((cb) => { subs.add(cb); return () => { subs.delete(cb) } }, () => state)
}
export const getGeoSettings = () => state

/* ── address lookup ── */
export interface AddressHit {
  label: string
  street: string
  postal_code: string
  city: string
  country_code: string
  lat: number
  lng: number
}

/** The offline demo book — deterministic for demos + e2e (provider 'builtin'). */
const BUILTIN: AddressHit[] = [
  { label: 'Frankrijklei 5, 2000 Antwerpen, BE', street: 'Frankrijklei 5', postal_code: '2000', city: 'Antwerpen', country_code: 'BE', lat: 51.2211, lng: 4.4175 },
  { label: 'Meir 24, 2000 Antwerpen, BE', street: 'Meir 24', postal_code: '2000', city: 'Antwerpen', country_code: 'BE', lat: 51.2186, lng: 4.4095 },
  { label: 'Noorderlaan 98, 2030 Antwerpen, BE', street: 'Noorderlaan 98', postal_code: '2030', city: 'Antwerpen', country_code: 'BE', lat: 51.2465, lng: 4.4137 },
  { label: 'Rue de la Loi 16, 1000 Bruxelles, BE', street: 'Rue de la Loi 16', postal_code: '1000', city: 'Bruxelles', country_code: 'BE', lat: 50.8455, lng: 4.3668 },
  { label: 'Grote Markt 1, 2000 Antwerpen, BE', street: 'Grote Markt 1', postal_code: '2000', city: 'Antwerpen', country_code: 'BE', lat: 51.2213, lng: 4.3997 },
  { label: 'Bondgenotenlaan 20, 3000 Leuven, BE', street: 'Bondgenotenlaan 20', postal_code: '3000', city: 'Leuven', country_code: 'BE', lat: 50.8809, lng: 4.7040 },
  { label: 'Korenmarkt 11, 9000 Gent, BE', street: 'Korenmarkt 11', postal_code: '9000', city: 'Gent', country_code: 'BE', lat: 51.0554, lng: 3.7215 },
  { label: 'Avenue J.F. Kennedy 43, 1855 Luxembourg, LU', street: 'Avenue J.F. Kennedy 43', postal_code: '1855', city: 'Luxembourg', country_code: 'LU', lat: 49.6295, lng: 6.1706 },
  { label: 'Weena 505, 3013 AL Rotterdam, NL', street: 'Weena 505', postal_code: '3013 AL', city: 'Rotterdam', country_code: 'NL', lat: 51.9244, lng: 4.4700 },
]

interface NominatimHit {
  display_name: string; lat: string; lon: string
  address?: { road?: string; house_number?: string; postcode?: string; city?: string; town?: string; village?: string; country_code?: string }
}

/** A provider failure is not "no results". The editor uses this distinction
 * to keep an unavailable service from looking like an inert search box. */
export class AddressLookupError extends Error {
  constructor(message = 'The address provider is unavailable.') {
    super(message)
    this.name = 'AddressLookupError'
  }
}

const CACHE_TTL_MS = 10 * 60_000
const REQUEST_FLOOR_MS = 1_050
const resultCache = new Map<string, { until: number; hits: AddressHit[] }>()
let nominatimQueue: Promise<void> = Promise.resolve()
let lastNominatimRequestAt = 0

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

async function searchNominatim(query: string): Promise<AddressHit[]> {
  const key = query.toLocaleLowerCase()
  const cached = resultCache.get(key)
  if (cached && cached.until > Date.now()) return cached.hits

  let release!: () => void
  const previous = nominatimQueue
  nominatimQueue = new Promise<void>((resolve) => { release = resolve })
  await previous

  try {
    const filledWhileQueued = resultCache.get(key)
    if (filledWhileQueued && filledWhileQueued.until > Date.now()) return filledWhileQueued.hits
    const remaining = REQUEST_FLOOR_MS - (Date.now() - lastNominatimRequestAt)
    if (remaining > 0) await wait(remaining)
    lastNominatimRequestAt = Date.now()

    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(query)}`,
      { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new AddressLookupError(`Address lookup failed (${res.status}).`)

    const payload = await res.json()
    if (!Array.isArray(payload)) throw new AddressLookupError()
    const hits = (payload as NominatimHit[]).map((h) => ({
      label: h.display_name,
      street: [h.address?.road, h.address?.house_number].filter(Boolean).join(' ')
        || h.display_name.split(',')[0]?.trim() || '',
      postal_code: h.address?.postcode ?? '',
      city: h.address?.city ?? h.address?.town ?? h.address?.village ?? '',
      country_code: (h.address?.country_code ?? '').toUpperCase(),
      lat: Number(h.lat), lng: Number(h.lon),
    })).filter((h) => h.label && h.street && Number.isFinite(h.lat) && Number.isFinite(h.lng))
    resultCache.set(key, { until: Date.now() + CACHE_TTL_MS, hits })
    if (resultCache.size > 50) resultCache.delete(resultCache.keys().next().value as string)
    return hits
  } catch (error) {
    if (error instanceof AddressLookupError) throw error
    throw new AddressLookupError()
  } finally {
    release()
  }
}

export async function lookupAddress(q: string, s: GeoSettings = state): Promise<AddressHit[]> {
  const query = q.trim()
  if (query.length < 3 || s.lookupProvider === 'none') return []
  if (s.lookupProvider === 'builtin') {
    const t = query.toLowerCase()
    return BUILTIN.filter((a) => a.label.toLowerCase().includes(t)).slice(0, 6)
  }
  // The public service permits deliberate end-user searches but explicitly
  // forbids client-side autocomplete. Callers invoke this branch only on an
  // explicit Search, Attach or Locate action. Calls are serialised and cached.
  return searchNominatim(query)
}

/** Embed URL for the map pane — OSM, keyless. There is no keyed branch: an embed
 *  URL puts its API key in the page, which a governed secret cannot be (#524). */
export function mapEmbedUrl(lat: number, lng: number, s: GeoSettings = state): string | null {
  if (s.mapProvider === 'none') return null
  const d = 0.004
  const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`
}
